import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Input } from 'src/design/atoms/Input';
import { NumberInput } from 'src/design/atoms/NumberInput';
import { Radio } from 'src/design/atoms/Radio';
import { Textarea } from 'src/design/atoms/Textarea';
import { TextInput } from 'src/design/atoms/TextInput';
import { FormikErrors, FormikTouched } from 'formik';
import React from 'react';
import {
  CTCJobSelector,
  ICTCJobSelectProps,
} from 'src/app/selectors/CTCJobSelector';
import { DocumentTemplateSelector } from 'src/app/selectors/DocumentTemplateSelector';
import { MilestoneTemplateSelector } from 'src/app/selectors/MilestoneTemplateSelector';
import { PlaceSelector } from 'src/app/selectors/PlaceSelector';
import { ChangeTemplateSelector } from 'src/app/selectors/ChangeTemplateSelector';
import { CustomerSelector } from './CustomerSelector';
import { ICreateJobDialogFormValues } from './CreateJobDialog';

export interface IBasicInfoTabProps {
  values: ICreateJobDialogFormValues;
  touched: FormikTouched<ICreateJobDialogFormValues>;
  errors: FormikErrors<ICreateJobDialogFormValues>;
  setFieldValue: (field: string, value: unknown) => void;
  setFieldTouched: (field: string, touched?: boolean) => void;
  onCTCJobChange: ICTCJobSelectProps['onChange'];
  activeTab: string | null;
  setActiveTab: (tab: string | null) => void;
  saveError: string | null;
}

const getType = (v?: string) => {
  if (!v) return 'N/A';
  const items = v.split('-');
  return items[1] ? items[1] : 'N/A';
};

export const BasicInfoTab: React.FC<IBasicInfoTabProps> = ({
  values,
  touched,
  errors,
  setFieldValue,
  setFieldTouched,
  onCTCJobChange,
  activeTab,
  setActiveTab,
  saveError,
}) => {
  return (
    <Stack gap="md">
      <Input.Wrapper
        label="CTC Job"
        withAsterisk
        error={touched.jobNumber && errors.jobNumber}
      >
        <CTCJobSelector
          ctcJobId={values.jobNumber}
          onChange={(job) => {
            onCTCJobChange(job);
            setFieldTouched('jobNumber', true);
          }}
          hideLabel
          small
        />
      </Input.Wrapper>

      <Input.Wrapper
        label="Customer"
        withAsterisk
        error={touched.customerNumber && errors.customerNumber}
      >
        <CustomerSelector
          customerId={values.customerNumber}
          onLoadComplete={(customer) => {
            setFieldValue('customerName', customer.custName);
            setFieldTouched('customerNumber', true);
            setFieldTouched('customerName', true);
          }}
        />
      </Input.Wrapper>

      <TextInput
        label="Division"
        placeholder="Derived from CTC Job"
        value={values.division?.name || ''}
        disabled
      />

      <TextInput
        label="Job Type"
        placeholder="Derived from CTC Job"
        value={getType(values.division?.name)}
        disabled
      />

      <Textarea
        label="Scope of Work"
        withAsterisk
        placeholder="Enter a scope of work for your new job..."
        value={values.description}
        onChange={(e) => setFieldValue('description', e.target.value)}
        onBlur={() => setFieldTouched('description', true)}
        error={touched.description && errors.description}
        minRows={3}
        autosize
      />

      <Group grow>
        <Radio.Group
          label="Tax Exempt"
          value={values.taxExempt ? 'true' : 'false'}
          onChange={(value) => setFieldValue('taxExempt', value === 'true')}
        >
          <Group p="xs">
            <Radio label="Yes" value="true" />
            <Radio label="No" value="false" />
          </Group>
        </Radio.Group>

        <Radio.Group
          label="Vendor Discounts"
          value={values.vendorDiscounts ? 'true' : 'false'}
          onChange={(value) =>
            setFieldValue('vendorDiscounts', value === 'true')
          }
        >
          <Group p="xs">
            <Radio label="Yes" value="true" />
            <Radio label="No" value="false" />
          </Group>
        </Radio.Group>
      </Group>

      <Group grow>
        <Radio.Group
          label="Per Diem"
          value={values.perDiemEnabled ? 'true' : 'false'}
          onChange={(value) => {
            setFieldValue('perDiemEnabled', value === 'true' ? true : false);
          }}
        >
          <Group p="xs">
            <Radio label="Yes" value="true" />
            <Radio label="No" value="false" />
          </Group>
        </Radio.Group>

        <Radio.Group
          label="Scale Pay"
          value={values.scalePayEnabled ? 'true' : 'false'}
          onChange={(value) => {
            const enabled = value === 'true';
            setFieldValue('scalePayEnabled', enabled);
            if (!enabled) {
              setFieldValue('classificationRates', []);
              if (activeTab === 'classifications') {
                setActiveTab('basic');
              }
            }
          }}
        >
          <Group p="xs">
            <Radio label="Yes" value="true" />
            <Radio label="No" value="false" />
          </Group>
        </Radio.Group>
      </Group>

      {values.perDiemEnabled && (
        <NumberInput
          label="Per Diem Amount"
          withAsterisk
          placeholder="Enter per diem amount..."
          value={values.perDiemAmount || undefined}
          onChange={(value) => setFieldValue('perDiemAmount', value)}
          onBlur={() => setFieldTouched('perDiemAmount', true)}
          error={touched.perDiemAmount && errors.perDiemAmount}
          min={0}
          decimalScale={2}
          fixedDecimalScale
          prefix="$"
        />
      )}

      <Input.Wrapper label="Change Template">
        <ChangeTemplateSelector
          value={values.changeTemplateId}
          onChange={(id) => setFieldValue('changeTemplateId', id)}
          label={null}
        />
      </Input.Wrapper>

      <Input.Wrapper label="Document Template">
        <DocumentTemplateSelector
          value={values.documentTemplateId}
          onChange={(id) => setFieldValue('documentTemplateId', id)}
          label={null}
        />
      </Input.Wrapper>

      <Input.Wrapper label="Milestone Template">
        <MilestoneTemplateSelector
          value={values.milestoneTemplateId}
          onChange={(id) => setFieldValue('milestoneTemplateId', id)}
          label={null}
        />
      </Input.Wrapper>

      <Input.Wrapper label="Jobsite Location" withAsterisk>
        <PlaceSelector
          initialValue={values.location}
          onChange={(location) => {
            setFieldValue('location', location);
            setFieldTouched('location', true);
          }}
        />
      </Input.Wrapper>

      {saveError && (
        <Text c="red" size="sm">
          Error saving. Job with job number {values.jobNumber} may already
          exist. Error message: {saveError}
        </Text>
      )}
    </Stack>
  );
};
